<template>
  <v-container fluid grid-list-sm>
    <v-layout row wrap>
      <v-flex dflex sm12>
        <v-card>
          <v-toolbar color="primary" dark>
            <v-toolbar-title
              >Audit Summary: {{ currentAudit.name }}</v-toolbar-title
            >
            <v-spacer></v-spacer>
            <DownloadButton
              icon="cloud_download"
              title="PDF"
              :filename="filenamePdf"
              :action="generatePdf"
              :disabled="!getIsOnline"
              v-if="hasPdfPreview"
              @download-error="onDownloadError"
            />
            <DownloadButton
              icon="cloud_download"
              title="XLSX"
              :filename="filenameXlsx"
              :action="generateXlsx"
              :disabled="!getIsOnline"
              v-if="hasTemplate"
              @download-error="onDownloadError"
            />
          </v-toolbar>
          <v-card-text v-if="currentAuditScore.customResult">
            <v-card flat>
              <v-card-text class="d-inline-flex">
                <span class="headline"
                  >QK: {{ currentAuditScore.customResult.qk }}</span
                >
                <span class="title"
                  >Punkte: {{ currentAuditScore.customResult.pkt }}</span
                >
                <span class="title"
                  >Beanstandungen:
                  {{ currentAuditScore.customResult.count }}</span
                >
              </v-card-text>
            </v-card>
          </v-card-text>
          <v-card-text>
            <AuditScore :auditScore="currentCompliance" />
          </v-card-text>
          <v-card-text>
            <AuditProgress :auditProgress="auditProgress" />
          </v-card-text>
          <v-card-text>
            <v-card flat>
              <v-card-text>
                Auditfazit:
                <v-textarea
                  outline
                  readonly
                  prepend-inner-icon="lock"
                  title="Schreibgeschützt, zum editieren in die Nachbereitung wechseln."
                  color="primary"
                  placeholder="Es wurde bislang kein Auditfazit hinterlegt"
                  :value="currentAudit.summary"
                ></v-textarea>
              </v-card-text>
            </v-card>
          </v-card-text>
        </v-card>
      </v-flex>

      <!--<v-flex xs12 d-flex>-->

      <v-layout row wrap>
        <v-flex d-flex sm12 md6>
          <AuditAnalysisComponent :auditanalysis="currentAuditSummary" />
        </v-flex>

        <v-flex sm12 md6>
          <v-card>
            <v-toolbar color="primary" dark>
              <v-toolbar-title>Audit Chart</v-toolbar-title>
            </v-toolbar>
            <v-card-text>
              <v-flex xs12>
                <AuditRadarChart :chartData="currentAuditRadar" />
              </v-flex>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>
    </v-layout>

    <AuditProcessContainer>
      <GroupableItemList
        slot="container_content"
        :list-items="auditItems"
        list-config-key="auditReporting"
        :manipulators="auditItemManipulators"
        @change="onListContentChanged"
      >
        <template v-slot:default="{ item }">
          <AuditItemRow :key="item.id">
            <ItemLayout
              slot="question"
              :severity="getSeverity(item.question.importance, 'audititem')"
              highlight-field-display-name="value"
              :hoverdisabled="true"
            >
              <AuditQuestionComponent
                slot="content"
                :question="item.question"
                :standard-id="currentAudit.standard"
              />
            </ItemLayout>

            <AuditFindings
              v-for="deviation in orderFindingsByImportance(item.deviations)"
              :key="deviation.id"
              slot="findings"
            >
              <ItemLayout
                slot="finding"
                :severity="getSeverity(deviation.importance, 'finding')"
                highlight-field-display-name="value"
                :hoverdisabled="true"
              >
                <AuditFindingComponent
                  slot="content"
                  :context="{
                    ref: {
                      auditId,
                      auditItemId: item.id,
                      findingId: deviation.id
                    }
                  }"
                  :deviation="deviation"
                />
              </ItemLayout>

              <ItemLayout
                v-for="(measure, measure_index) in getFindingMeasureMapping[
                  deviation.id
                ]"
                :key="measure_index"
                slot="measures"
                :hoverdisabled="true"
                highlight-field-display-name="short"
                :severity="getSeverity(measure.measuretype, 'measure')"
              >
                <AuditMeasureComponent slot="content" :measure="measure" />
              </ItemLayout>
            </AuditFindings>
          </AuditItemRow>
        </template>
      </GroupableItemList>
    </AuditProcessContainer>
  </v-container>
</template>

<i18n>
{
  "de": {
    "audititem_header": "Auditfrage",
    "auditfinding_header": "Feststellung",
    "auditmeasure_header": "Maßnahme",
    "auditquestion": "Auditfrage",
    "auditfinding": "Feststellung",
    "category": "Kategorie",
    "chapter" : "Kapitel",
    "measure": "Maßnahme",
    "create_finding": "Beschreibung hinzufügen",
    "create_measure": "Maßnahme hinzufügen",
    "finding_9": "Hauptabweichung",
    "finding_3": "Nebenabweichung",
    "finding_1": "Anmerkung",
    "finding_0": "Bemerkung"
  },
  "en": {
    "auditquestion": "Auditfrage",
    "auditfinding": "Auditfinding",
    "category": "Category",
    "chapter": "Kapitel",
    "measure": "Measure",
    "create_finding": "Assing Audit Finding",
    "create_measure": "Create Measure",
    "finding_9": "Major Nonconformity",
    "finding_3": "Nonconformity",
    "finding_1": "Anmerkung",
    "finding_0": "Remark"
  }
}
</i18n>

<script>
import _ from "lodash";

import { mapGetters, mapActions } from "vuex";
import { api } from "@/store/modules/audit";
import { api as measureApi } from "@/store/modules/audits";
import { api as configApi } from "@/store/modules/configuration";
import { api as appApi } from "@/store/modules/app";
import { FILTER_FINDING_IMPORTANCE } from "@/utils/AuditItemListManipulatorIds";

import requestPdfReport from "@/utils/requestPdfReport";
import generateXlsxReport from "@/utils/generateXlsxReport";

// Analysies Compponentes
import AuditRadarChart from "@/components/charts/AuditRadarChart.vue";
import AuditAnalysisComponent from "@/components/AuditAnalysisComponent.vue";

// Layouts
import AuditProcessContainer from "@/components/layouts/BaseLayouts/AuditProcessContainer.vue";
import AuditItemRow from "@/components/layouts/AuditWrapUp/AuditItemRow.vue";
import AuditFindings from "@/components/layouts/AuditWrapUp/AuditFindings.vue";
import ItemLayout from "@/components/layouts/BaseLayouts/ItemLayout.vue";
import GroupableItemList from "@/components/layouts/BaseLayouts/GroupableItemList";

// Controls/Snippets
import AuditProgress from "@/components/snippets/AuditProgress.vue";
import AuditScore from "@/components/snippets/AuditScore.vue";
import DownloadButton from "@/components/controls/DownloadButton";

// Components
import AuditQuestionComponent from "@/components/widgets/AuditQuestionComponent.vue";
import AuditFindingComponent from "@/components/widgets/AuditFindingComponent.vue";
import AuditMeasureComponent from "@/components/widgets/AuditMeasureComponent.vue";

import AuditItemListMixin from "@/components/mixins/AuditItemListMixin";
import AuditReportingMixin from "@/components/mixins/AuditReportingMixin";
import { formatDates } from "@/components/mixins/FormattingMixin";

const scoreToCompliancePercent = score => {
  const total_range = score.best - score.worst;
  const current_range = score.current - score.worst;
  const compliance = current_range / total_range;
  return compliance;
};

export default {
  components: {
    AuditRadarChart,
    AuditAnalysisComponent,
    AuditProcessContainer,
    AuditItemRow,
    ItemLayout,
    AuditFindings,
    AuditProgress,
    AuditScore,
    AuditQuestionComponent,
    AuditFindingComponent,
    AuditMeasureComponent,
    GroupableItemList,
    DownloadButton
  },
  mixins: [AuditItemListMixin, AuditReportingMixin],
  data: () => ({
    manipulatedListItems: null
  }),
  computed: {
    ...mapGetters(api.namespace, [
      api.getters.getAuditProgress,
      api.getters.getAuditScore,
      api.getters.getFlatExportData,
      api.getters.getAuditReportSummary,
      api.getters.getAuditStatistics
    ]),

    ...mapGetters(measureApi.namespace, [
      measureApi.getters.getFindingMeasureMapping,
      measureApi.getters.getMeasuresByAuditId
    ]),

    ...mapGetters(configApi.namespace, [
      configApi.getters.audit,
      configApi.getters.finding,
      configApi.getters.categories,
      configApi.getters.audititem,
      configApi.getters.hasPdfPreview,
      configApi.getters.customerLogo
    ]),
    ...mapGetters(appApi.namespace, [appApi.getters.getIsOnline]),

    filenamePdf() {
      return (
        "Report_" +
        this.moment().format("YYYY-MM-DD") +
        "_" +
        this.currentAudit.name +
        ".pdf"
      );
      // return "Report.pdf";
    },
    filenameXlsx() {
      return (
        "Report_" +
        this.moment().format("YYYY-MM-DD") +
        "_" +
        this.currentAudit.name +
        ".xlsx"
      );
    },
    filteredAndSortedAuditItems() {
      if (this.manipulatedListItems && this.manipulatedListItems.ListItems) {
        console.log(
          "filteredAndSortedAuditItems:filtered",
          this.manipulatedListItems.ListItems
        );
        return this.manipulatedListItems.ListItems;
      } else {
        console.log("filteredAndSortedAuditItems", this.auditItems);
        return this.auditItems;
      }
    },
    filteredFindingImportance() {
      let FindingTypes = null;

      if (
        this.manipulatedListItems &&
        this.manipulatedListItems.ManipulatorParams &&
        this.manipulatedListItems.ManipulatorParams.filter_by
      ) {
        const filter = this.manipulatedListItems.ManipulatorParams.filter_by.find(
          filter_by => filter_by.id === FILTER_FINDING_IMPORTANCE
        );
        if (filter) {
          FindingTypes = filter.params;
        }
      }
      return FindingTypes;
    },
    companyMetadata() {
      return this.currentAudit.companyMetadata || null;
    },
    additionalReportData() {
      const data = this.currentAudit.additionalReportData || null;
      if (data) {
        return {
          correctionDueDate: formatDates(data.correctionDueDate, null)[0],
          reviewDueDate: formatDates(data.reviewDueDate, null)[0],
          requiresFollowUp:
            typeof data.requiresFollowUp === "string"
              ? formatDates(data.requiresFollowUp, null)[0]
              : data.requiresFollowUp
        };
      } else {
        return null;
      }
    },
    exportData() {
      return {
        auditName: this.currentAudit.name,
        customerLogo: this.customerLogo,
        exportDate: this.moment().format("l"),
        auditPlaningYear: this.currentAudit.planning_year,
        auditingDates: formatDates(this.currentAudit.auditing_date, null),
        auditStandards: this.auditStandard,
        auditType: this.currentAudit.type,
        auditBy: this.currentAudit.audited_by,
        leadAuditor: this.currentAuditReportSummary.leadAuditor,
        responsibleForMeasures: this.currentAuditReportSummary.responsibleUser,
        coAuditors: this.currentAuditReportSummary.auditCoAuditors, // ["Markus Meier", "Erna Etna"],
        participants: this.currentAuditReportSummary.auditParticipants, // this.currentAudit.members, ["Markus Meier", "Erna Etna", "Frowin Zaun"],
        auditSummary: this.currentAudit.summary,
        additionalReportData: this.additionalReportData,
        company_metadata: this.companyMetadata,

        statistics: this.getAuditStatistics.statistics,
        score: {
          ...this.currentCalcScore
        },
        compliancePercent: this.complianceScore,
        findings: this.flatFindingsList
      };
    },
    generatePdf() {
      console.log("generatePdf");
      return requestPdfReport(this.exportData, this.reportTemplateName);
    },
    generateXlsx() {
      console.log("generateXlsx");
      return generateXlsxReport(this, this.flatFindingsList);
    },

    currentAuditReportSummary() {
      const result = this.getAuditReportSummary;
      return result;
    },
    currentExcelExport() {
      const result = this.currentAuditReportSummary;

      let output = [["Key", "Value"]];
      const scalar_fields = [
        "auditId",
        "auditName",
        "auditReportingType",
        "auditStandard",
        "auditType",
        "auditedBy",
        "auditPlaningDate",
        "auditPlaningYear",
        "auditingDates"
      ];
      for (let key of scalar_fields) {
        output.push([key, result[key]]);
      }

      const statistics_fields = [
        "auditItemCount",
        "auditFindingsCount",
        "auditMeasuresCount"
      ];
      for (let key of statistics_fields) {
        output.push([key, result.statistics[key]]);
      }
      output.push([]);
      output.push([
        "Category",
        "worst Score",
        "current Score",
        "best Score",
        "Compliance %"
      ]);

      output.push([
        "Total Score",
        result.score.inTotal.worst,
        result.score.inTotal.current,
        result.score.inTotal.best,
        scoreToCompliancePercent(result.score.inTotal)
      ]);
      output.push([]);
      for (let score of result.score.byCategory) {
        output.push([
          score.name,
          score.worst,
          score.current,
          score.best,
          scoreToCompliancePercent(score)
        ]);
      }
      return output;
    },
    auditProgress() {
      return this.getAuditProgress;
    },
    currentAuditScore() {
      const current_audit_score = this.getAuditScore;
      console.log("current_audit_score", current_audit_score);
      return current_audit_score;
    },
    currentCompliance() {
      return Math.round(this.currentCalcScore.compliancePercent * 100);
    },
    currentAuditRadar() {
      const score = this.currentCalcScore;
      const chart = score.byCategory;

      const trim_length = 20;
      const currentLabels = _.map(chart, val => {
        const category = { name: val.auditItemCategoryName };

        if (category) {
          const name = category.name;
          if (name.length > trim_length) {
            return name.substring(0, trim_length) + "…";
          } else {
            return name;
          }
        } else {
          return val.name;
        }
      });
      const currentValues = _.map(chart, score => {
        return Math.round(score.compliancePercent * 100);
      });

      // TODO: Sollen nur die berechneten Kategorien genutzt werden oder alle die nutzbar wären?
      //const categories = this.getCurrentAuditCategories
      // Was machen wir, wenn es nur eine Cat gibt? Dann macht ein Spinnennetzdiagramm einfach keinen Sinn!

      const graph = {
        labels: currentLabels,
        datasets: [
          {
            label: "Erfüllungsgrad",
            borderColor: "#b2ebf2",
            data: currentValues
          }
        ]
      };
      console.log("Radar", graph, score);
      return graph;
    },
    flatFindingsList() {
      const audit_findings = this.getFlatExportData(
        this.filteredAndSortedAuditItems,
        this.filteredFindingImportance
      );
      console.log("Findings", audit_findings);

      return audit_findings;
    },
    hasTemplate() {
      return typeof this.audit.exportTemplate === "string";
    },
    reportTemplateName() {
      return this.audit.reportTemplateName || "report";
    }
  },
  methods: {
    ...mapActions(appApi.namespace, [appApi.actions.addStatus]),
    async onDownloadError(err) {
      console.log("Download failed", err);
      this.addStatus({ message: "Download failed", type: "error" });
    },
    onListContentChanged(payload) {
      console.log("onListContentChanged", this.manipulatedListItems, payload);
      this.manipulatedListItems = payload;
    }
  }
};
</script>
